import { useMemo } from "react";
import { FormControl, FormHelperText } from "@mui/material";
import { useTranslation } from "react-i18next";
import { FoodsSelectServerPagination } from "@foodpilot/foods";
import { useDelayedSearch } from "src/utils/useDelayedSearch";
import { useDataListSelector } from "src/api/sure-api/dataList/useDataListSelector";
import { DataList } from "src/api/sure-api/dataList/useGetAllDataList";

interface DataListAutocompleteProps {
    value: DataList | null;
    onChange: (option: DataList | null) => void;
    placeholder?: string;
    disabled?: boolean;
    error?: boolean;
    helperText?: string;
}

export function DataListAutocomplete({
    value,
    onChange,
    placeholder,
    disabled = false,
    error = false,
    helperText,
}: DataListAutocompleteProps) {
    const { t } = useTranslation();
    const { search, delayedSearch, setSearch } = useDelayedSearch();

    const { dataLists = [], isLoading } = useDataListSelector({
        searchText: delayedSearch,
        enabled: true,
        itemsPerPage: 50,
    });

    const filteredDataLists = useMemo(() => {
        let filtered = dataLists;

        if (value && !filtered.some((dataList) => dataList.id === value.id)) {
            filtered = [value, ...filtered];
        }

        return filtered;
    }, [dataLists, value]);

    return (
        <FormControl fullWidth error={error}>
            <FoodsSelectServerPagination
                selectedOption={value}
                options={isLoading ? [] : filteredDataLists}
                onChange={(dataList) => onChange(dataList ?? null)}
                getId={(dataList: DataList) => dataList.id}
                getName={(dataList: DataList) => dataList.code}
                textOptions={{
                    placeholder: placeholder || t("data_list.select.placeholder", "Select a data list"),
                    searchPlaceholder: t("data_list.select.searchPlaceholder", "Search data lists..."),
                    emptyOptionsText: t("data_list.select.noOptions", "No data lists available"),
                    defaultValueText: t("data_list.select.defaultValue", "Default value"),
                    noResult:
                        isLoading ?
                            t("data_list.select.loading", "Loading...")
                        :   t("data_list.select.noResultFound", "No data lists found"),
                }}
                search={search}
                setSearch={setSearch}
                isSearchFound={() => true}
                disabled={disabled}
                BoxProps={{ width: "100%" }}
                BoxOptionsProps={{
                    minWidth: "300px",
                    width: "50%",
                }}
            />
            {helperText ?
                <FormHelperText>{helperText}</FormHelperText>
            :   null}
        </FormControl>
    );
}
